import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { setTokenGetter } from '../utils/authFetch';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  return ctx || { user: null, isAuthenticated: false, loading: true, permissions: null, login: () => {}, register: () => {}, logout: () => {}, hasPermission: () => false, canAccessTab: () => false };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  const getToken = useCallback(() => tokenRef.current, []);

  // Wire token getter into authFetch utility
  useEffect(() => { setTokenGetter(getToken); }, [getToken]);

  const fetchPermissions = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/rbac/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPermissions(data);
        return data;
      }
    } catch (error) { console.error('Permission fetch failed:', error.message); }
    return null;
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Try cookie-based auth first (httpOnly cookie set by backend)
        const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
          // Try to refresh token for Bearer header use
          const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
          if (refreshRes.ok) {
            const refreshData = await refreshRes.json();
            tokenRef.current = refreshData.token;
            await fetchPermissions(refreshData.token);
          }
        }
      } catch (error) { console.error('Auth check failed:', error.message); }
      finally { setLoading(false); }
    };
    checkAuth();
  }, [fetchPermissions]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) {
      const detail = data.detail;
      throw new Error(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(' ') : 'Login failed');
    }
    tokenRef.current = data.token;
    setUser(data);
    await fetchPermissions(data.token);
    return data;
  }, [fetchPermissions]);

  const register = useCallback(async (email, password, name) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name })
    });
    const data = await res.json();
    if (!res.ok) {
      const detail = data.detail;
      throw new Error(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(' ') : 'Registration failed');
    }
    tokenRef.current = data.token;
    setUser(data);
    await fetchPermissions(data.token);
    return data;
  }, [fetchPermissions]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (error) { console.error('Logout failed:', error.message); }
    tokenRef.current = null;
    setUser(null);
    setPermissions(null);
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!permissions) return true;
    if (permissions.permissions.includes('*')) return true;
    return permissions.permissions.includes(perm);
  }, [permissions]);

  const canAccessTab = useCallback((tabId) => {
    if (!permissions) return true;
    return permissions.accessible_tabs.includes(tabId);
  }, [permissions]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, loading, permissions, getToken, login, register, logout, hasPermission, canAccessTab }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
